import React from "react";
import { motion } from "framer-motion";
import { Heart, ArrowDown } from "lucide-react";
import { heroData } from "../data";
import { staggerContainer, fadeIn } from "../variants";
import HeroImg from "../img/hero/eleves01.jpg";

const Hero = () => {
  const { eyebrow, title, subtitle, btnPrimary, btnSecondary } = heroData;

  return (
    <section className="relative min-h-screen flex items-center justify-start overflow-hidden">
      {/* Background image */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat scale-105"
        style={{
          backgroundImage: `url(${HeroImg})`,
          willChange: "transform",
        }}
      />

      {/* Gradient overlay — story-driven darkness */}
      <div className="absolute inset-0 bg-gradient-to-r from-dark/90 via-dark/60 to-dark/20" />
      <div className="absolute inset-0 bg-gradient-to-t from-dark/80 via-transparent to-dark/20" />

      {/* Content */}
      <div className="relative container mx-auto py-32 lg:py-0">
        <motion.div
          variants={staggerContainer(0.15, 0.3)}
          initial="hidden"
          animate="show"
          className="max-w-3xl"
        >
          {/* Eyebrow */}
          <motion.div variants={fadeIn("up", 0)} className="mb-6">
            <span className="inline-flex items-center gap-2 bg-secondary/20 border border-secondary/40 text-secondary font-display text-xs font-semibold tracking-widest uppercase px-4 py-2 rounded-full backdrop-blur-sm">
              <span className="w-1.5 h-1.5 rounded-full bg-secondary animate-pulse" />
              {eyebrow}
            </span>
          </motion.div>

          {/* Main title */}
          <motion.h1
            variants={fadeIn("up", 0.1)}
            className="font-heading text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold text-white leading-none tracking-tight mb-8"
          >
            {title}
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            variants={fadeIn("up", 0.2)}
            className="text-white/80 text-lg lg:text-xl leading-relaxed max-w-xl mb-12"
          >
            {subtitle}
          </motion.p>

          {/* CTAs */}
          <motion.div
            variants={fadeIn("up", 0.3)}
            className="flex flex-col sm:flex-row gap-4"
          >
            <a
              href={btnPrimary.href}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-3 bg-secondary hover:bg-secondary-dark text-white font-semibold text-base px-8 py-4 rounded-full transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-1 group"
            >
              <Heart size={18} fill="currentColor" className="group-hover:scale-110 transition-transform" />
              {btnPrimary.text}
            </a>
            <a
              href={btnSecondary.href}
              className="inline-flex items-center justify-center gap-3 border-2 border-white/70 text-white hover:bg-white hover:text-primary font-semibold text-base px-8 py-4 rounded-full transition-all duration-300 backdrop-blur-sm"
            >
              {btnSecondary.text}
              <ArrowDown size={16} />
            </a>
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
      >
        <span className="text-white/50 text-xs font-body tracking-widest uppercase">Défiler</span>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 1.6, ease: "easeInOut" }}
          className="w-5 h-8 rounded-full border-2 border-white/30 flex items-start justify-center pt-1.5"
        >
          <div className="w-1 h-2 bg-white/60 rounded-full" />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
